import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { TableHead } from '@mdvip/shared/components/ui/table';
import { cn } from '@mdvip/shared/lib/utils';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortableTableHeadProps extends Omit<React.ComponentProps<'th'>, 'children'> {
  /** The column's current sort. `null` means sortable but not the active sort column. */
  direction: SortDirection;
  onSort: () => void;
  /** Column label. */
  children: React.ReactNode;
}

const ARIA_SORT = { asc: 'ascending', desc: 'descending', none: 'none' } as const;
const GLYPH = { asc: 'sortUp', desc: 'sortDown', none: 'sort' } as const;

/**
 * SortableTableHead — a `TableHead` whose label is a button that asks the table to re-sort.
 *
 * Presentational only: it renders whatever `direction` it's given and reports the click. The sort
 * cycle (asc ↔ desc, or a third "off" step) belongs to the table that owns the rows, so tables with
 * different sort rules can share this header.
 */
export function SortableTableHead({ direction, onSort, children, className, ...props }: SortableTableHeadProps) {
  const { t } = useTranslation('shared');
  const state = direction ?? 'none';
  const ariaSort = ARIA_SORT[state];

  return (
    <TableHead aria-sort={ariaSort} className={className} {...props}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onSort}
        className={cn(
          '-mx-2 h-8 gap-1 px-2 text-xs font-medium',
          direction ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {children}
        <Icon name={GLYPH[state]} size="xs" />
        <span className="sr-only"> {t(`table.sort.${ariaSort}`)}</span>
      </Button>
    </TableHead>
  );
}
